import {
    LOAD_SIMILAR_SOLD_ITEMS_V3_FAIL,
    LOAD_SIMILAR_SOLD_ITEMS_V3_REQUEST,
    LOAD_SIMILAR_SOLD_ITEMS_V3_SUCCESS,
} from './actions';
import api from '@/redux/api/similarItems';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getUserExcludedHouses } from './searchExclusions';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import { SimilarItemInfo as SimilarItemInfoType } from '@/types/SimilarItemInfo';
import { transformSimilarItemsFromV3 } from '@/utils/similarItems';
import union from 'lodash/union';

const REDUX_STORE_TIME = ms('30m');

/* REDUCER */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SIMILAR_SOLD_ITEMS_V3_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.itemId]),
            };
        case LOAD_SIMILAR_SOLD_ITEMS_V3_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_SIMILAR_SOLD_ITEMS_V3_SUCCESS:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [action.meta.itemId]: action.payload.map((i) => i.itemId),
                },
                loaded: {
                    ...state.loaded,
                    [action.meta.itemId]: action.meta.actionTime,
                },
                loading: difference(state.loading, [action.meta.itemId]),
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.similarSoldItemsV3;

const idSelector = (state, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getSimilarItemIds = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);

const getLoadTime = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetch = (state, itemId) => {
    if (!itemId) {
        return false;
    }
    const item = getSimilarItemIds(state, itemId);
    if (item.length) {
        const loaded = getLoadTime(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isLoading(state, itemId);
    return !loading;
};

/* ACTION CREATORS */
const loadSimilarSoldItemsV3 = (similarItemInfo: SimilarItemInfoType) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const searchExclusions = getUserExcludedHouses(state);

        dispatch({
            payload: similarItemInfo.itemId,
            type: LOAD_SIMILAR_SOLD_ITEMS_V3_REQUEST,
        });

        const response = await api.fetchSimilarSoldItemsV3({
            authToken,
            deployment,
            searchExclusions,
            similarItemInfo,
        });
        const transformedItems = transformSimilarItemsFromV3(response.payload.items);
        return dispatch({
            meta: { actionTime: Date.now(), itemId: similarItemInfo.itemId },
            payload: transformedItems,
            type: LOAD_SIMILAR_SOLD_ITEMS_V3_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            meta: { itemId: similarItemInfo.itemId },
            payload: error,
            type: LOAD_SIMILAR_SOLD_ITEMS_V3_FAIL,
        });
    }
};

export const fetchSimilarSoldItemsV3IfNeeded = (similarItemInfo: SimilarItemInfoType) => async (
    dispatch: Function,
    getState: Function
) => {
    if (similarItemInfo?.itemId && shouldFetch(getState(), similarItemInfo.itemId)) {
        return dispatch(loadSimilarSoldItemsV3(similarItemInfo));
    }
    return Promise.resolve();
};
